import { useFocusEffect } from "@react-navigation/native"
import { collections, firestore, IUserContext, logger, useAlert, userContext } from "capsule"
import { useCallback, useContext, useEffect, useState } from "react"
import { useDocumentData } from "react-firebase-hooks/firestore"
import { useTranslation } from "react-i18next"
import { Linking, Platform } from "react-native"

import { PatientNS } from "../i18n/constants"
import { AppUserData } from "../models/UserData"
import { updateUserData } from "../models/UserDataFunctions"

// Seuils de patients étendus pour les différents types de professionnels de santé
const KINE_THRESHOLDS = [3, 7, 15, 30, 50]
const SURGEON_THRESHOLDS = [20, 50, 75, 100, 200, 300, 400]
const GP_THRESHOLDS = [10, 25, 50]

// IDs des stores
const APP_STORE_ID = "1574145501"
const PLAY_STORE_ID = "com.siruplab.doctup"

/**
 * Hook personnalisé pour gérer les demandes d'avis sur les stores
 * Vérifie le nombre de patients du professionnel de santé et affiche une popup
 * pour demander un avis selon des seuils prédéfinis (max 3 demandes)
 */
export const useStoreReviewRequest = () => {
  const { user, userDocRef } = useContext<IUserContext<AppUserData>>(userContext)
  const { showDialog } = useAlert()
  const { t } = useTranslation()
  const [previousPatientCount, setPreviousPatientCount] = useState<number | null>(null)
  const [isScreenFocused, setIsScreenFocused] = useState(false)

  // Observer le document utilisateur en temps réel
  const [userData] = useDocumentData<AppUserData>(
    user?.uid ? firestore().collection(collections.LOGIN).doc(user.uid) : null,
    { idField: "uid" },
  )

  // Détecter quand l'écran est visible/invisible
  useFocusEffect(
    useCallback(() => {
      setIsScreenFocused(true)
      return () => setIsScreenFocused(false)
    }, []),
  )

  // Fonction pour afficher la popup de demande d'avis
  const showReviewDialog = useCallback(
    (threshold: number) => {
      // Log du seuil pour éviter le warning de paramètre non utilisé
      logger("StoreReview - Showing popup for threshold:", threshold)

      showDialog({
        type: "button",
        title: t(`${PatientNS}:storeReview.title`),
        message: t(`${PatientNS}:storeReview.message`),
        dismissable: false,
        positive: {
          onPress: async () => {
            try {
              const storeUrl = Platform.select({
                ios: `itms-apps://apps.apple.com/app/id${APP_STORE_ID}?action=write-review`,
                android: `market://details?id=${PLAY_STORE_ID}`,
                default: "",
              })

              await Linking.openURL(storeUrl)
            } catch (error) {
              const webStoreUrl = Platform.select({
                ios: `https://apps.apple.com/app/${APP_STORE_ID}?action=write-review`,
                android: `https://play.google.com/store/apps/details?id=${PLAY_STORE_ID}`,
                default: "",
              })
              try {
                await Linking.openURL(webStoreUrl)
              } catch (e) {
                logger("Failed to open store review page", e)
              }
            }
          },
          label: t(`${PatientNS}:storeReview.accept`),
        },
        negative: {
          onPress: () => {},
          label: t(`${PatientNS}:storeReview.decline`),
        },
      })
    },
    [showDialog, t],
  )

  // Vérifier le nombre de patients quand il change
  const checkPatientThresholds = useCallback(
    async (hcp: AppUserData, patientCount: number) => {
      try {
        // Vérifier si l'utilisateur est un professionnel de santé
        if (hcp.role === "patient" || Platform.OS === "web") {
          return
        }

        // Sélectionner les seuils en fonction du rôle
        let thresholds: number[] = []
        if (hcp.role === "kine") {
          thresholds = KINE_THRESHOLDS
        } else if (hcp.role === "surgeon") {
          thresholds = SURGEON_THRESHOLDS
        } else if (hcp.role === "gp") {
          thresholds = GP_THRESHOLDS
        }

        // Récupérer les demandes d'avis déjà effectuées
        const storeReviewRequests = hcp.storeReviewRequests || []

        // Ne pas nettoyer le tableau pour conserver l'historique des demandes
        // Vérifier si on a déjà atteint la limite de 3 demandes d'avis (restriction Apple Store)
        if (storeReviewRequests.length >= 3) {
          return
        }

        // Cas 1: Première exécution (tableau vide) -> on identifie les seuils pertinents
        if (storeReviewRequests.length === 0) {
          // 1. Trouver le dernier seuil atteint ou dépassé
          const thresholdsReached = thresholds.filter(threshold => patientCount >= threshold)
          if (thresholdsReached.length === 0) {
            return // Pas encore atteint le premier seuil
          }

          const lastReachedThreshold = Math.max(...thresholdsReached)

          // 2. Demande d'avis pour le dernier seuil atteint
          await updateUserData(userDocRef, {
            storeReviewRequests: [lastReachedThreshold],
          })

          // Afficher la popup
          showReviewDialog(lastReachedThreshold)
        }
        // Cas 2: Exécutions suivantes -> on vérifie le prochain seuil dans l'ordre
        else if (storeReviewRequests.length < 3) {
          // Trouver le seuil le plus élevé déjà demandé
          const highestExistingThreshold = Math.max(...storeReviewRequests)

          // Trouver tous les seuils atteints qui sont supérieurs au dernier seuil demandé
          const newReachedThresholds = thresholds
            .filter(threshold => threshold > highestExistingThreshold && patientCount >= threshold)
            .sort((a, b) => a - b)

          if (newReachedThresholds.length > 0) {
            // Prendre le plus petit des nouveaux seuils atteints
            const nextThreshold = newReachedThresholds[0]

            // Ajouter ce seuil au tableau
            const updatedRequests = [...storeReviewRequests, nextThreshold]
            await updateUserData(userDocRef, {
              storeReviewRequests: updatedRequests,
            })

            // Afficher la popup
            showReviewDialog(nextThreshold)
          }
        }
      } catch (error) {
        logger("StoreReview - Error checking patient thresholds", error)
      }
    },
    [userDocRef, showReviewDialog],
  )

  // Surveiller les changements dans le document utilisateur (nombre de patients)
  useEffect(() => {
    // Ne faire la vérification que si l'écran est visible
    if (!userData || !isScreenFocused) {
      return
    }

    const currentPatientCount = userData.patients?.length || 0

    // Ne vérifier que si le nombre de patients a changé
    if (previousPatientCount !== currentPatientCount) {
      checkPatientThresholds(userData, currentPatientCount)
      setPreviousPatientCount(currentPatientCount)
    }
  }, [userData, previousPatientCount, isScreenFocused, checkPatientThresholds])
}
